import { t as tr } from './i18n.js'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type ProjectTimer } from './api.js'
import { Link } from 'react-router'
import { Pause, Play, Square } from 'lucide-react'

const IDLE_AFTER_MS = 5 * 60_000
const HEARTBEAT_MS = 60_000

interface TimerState {
  timers: ProjectTimer[]
  timer: ProjectTimer | null            // the running one
  elapsedFor: (t: ProjectTimer) => number
  elapsedSeconds: number                // running timer's elapsed
  idle: { since: string; minutes: number } | null
  start: (b: { projectId: string; taskId?: string | null; notes?: string | null; billable?: boolean }) => Promise<void>
  pause: (projectId: string) => Promise<void>
  stop: (projectId: string, b?: { endedAt?: string; notes?: string | null; discard?: boolean }) => Promise<void>
  update: (projectId: string, b: { taskId?: string | null; notes?: string | null; billable?: boolean }) => Promise<void>
  dismissIdle: () => void
  error: string | null
}
const Ctx = createContext<TimerState | null>(null)

/**
 * Per-project timers (Harvest-style): one running at a time; starting another
 * pauses it. Idle detection = no input in this tab for 5 minutes while a timer
 * runs; on return, the widget offers to keep / drop the idle stretch.
 */
export function TimerProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['timer'], queryFn: () => api<{ timers: ProjectTimer[]; running: ProjectTimer | null }>('/timers'), refetchInterval: 30_000 })
  const timers = q.data?.timers ?? []
  const timer = q.data?.running ?? null
  const fetchedAt = useRef(Date.now())
  useEffect(() => { fetchedAt.current = Date.now() }, [q.dataUpdatedAt])
  const [now, setNow] = useState(() => Date.now())
  const [idle, setIdle] = useState<TimerState['idle']>(null)
  const [error, setError] = useState<string | null>(null)
  const lastInput = useRef(Date.now())
  const idleSince = useRef<number | null>(null)
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t) }, [])

  useEffect(() => {
    if (!timer) { idleSince.current = null; return }
    const onInput = () => {
      const t = Date.now()
      if (idleSince.current !== null && t - idleSince.current > IDLE_AFTER_MS / 2) setIdle({ since: new Date(idleSince.current).toISOString(), minutes: Math.round((t - idleSince.current) / 60_000) })
      idleSince.current = null; lastInput.current = t
    }
    const check = () => { const t = Date.now(); if (idleSince.current === null && t - lastInput.current > IDLE_AFTER_MS) idleSince.current = lastInput.current }
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']
    for (const e of events) window.addEventListener(e, onInput, { passive: true })
    const onVis = () => { if (document.visibilityState === 'visible') onInput() }
    document.addEventListener('visibilitychange', onVis)
    const iv = setInterval(check, 15_000)
    const hb = setInterval(() => { if (Date.now() - lastInput.current < HEARTBEAT_MS * 2) void api('/timers/heartbeat', { method: 'POST' }).catch(() => {}) }, HEARTBEAT_MS)
    return () => { for (const e of events) window.removeEventListener(e, onInput); document.removeEventListener('visibilitychange', onVis); clearInterval(iv); clearInterval(hb) }
  }, [timer])

  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['timer'] }); void qc.invalidateQueries({ queryKey: ['time'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); void qc.invalidateQueries({ queryKey: ['tasks'] }) }
  const err = (e: unknown, f: string) => setError(e instanceof ApiError ? e.message : f)
  const startM = useMutation({ mutationFn: (b: Parameters<TimerState['start']>[0]) => api('/timers/start', { method: 'POST', json: b }), onSuccess: () => { setError(null); lastInput.current = Date.now(); invalidate() }, onError: (e) => err(e, 'Could not start timer') })
  const pauseM = useMutation({ mutationFn: (pid: string) => api(`/timers/${pid}/pause`, { method: 'POST' }), onSuccess: () => { setError(null); invalidate() }, onError: (e) => err(e, 'Could not pause') })
  const stopM = useMutation({ mutationFn: ({ pid, b }: { pid: string; b?: Parameters<TimerState['stop']>[1] }) => api(`/timers/${pid}/stop`, { method: 'POST', json: b ?? {} }), onSuccess: () => { setError(null); setIdle(null); invalidate() }, onError: (e) => err(e, 'Could not stop timer') })
  const updateM = useMutation({ mutationFn: ({ pid, b }: { pid: string; b: Parameters<TimerState['update']>[1] }) => api(`/timers/${pid}`, { method: 'PATCH', json: b }), onSuccess: invalidate, onError: (e) => err(e, 'Could not update timer') })

  const elapsedFor = (t: ProjectTimer) => t.running ? t.elapsedSeconds + Math.max(0, Math.floor((now - fetchedAt.current) / 1000)) : t.accumulatedSeconds
  const value: TimerState = {
    timers, timer, elapsedFor,
    elapsedSeconds: timer ? elapsedFor(timer) : 0,
    idle, error,
    start: async (b) => { await startM.mutateAsync(b) },
    pause: async (pid) => { await pauseM.mutateAsync(pid) },
    stop: async (pid, b) => { await stopM.mutateAsync({ pid, b }) },
    update: async (pid, b) => { await updateM.mutateAsync({ pid, b }) },
    dismissIdle: useCallback(() => setIdle(null), []),
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useTimer(): TimerState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useTimer outside TimerProvider')
  return v
}

export const fmtElapsed = (s: number) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

/** Top-bar "Active timers" pull-down: every timer with its running value and pause/resume/stop controls; idle prompt lives here too. */
export function TimersMenu() {
  const t = useTimer()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  if (t.timers.length === 0 && !t.idle) return null
  const run = t.timer
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  void isMac
  return (
    <div className="menu timersmenu" ref={ref}>
      <button className={`link plain menubtn ${open ? 'active' : ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-tip={run ? `Timer running on ${run.projectCode}; ${t.timers.length} timer${t.timers.length === 1 ? '' : 's'} in total` : `${t.timers.length} paused timer${t.timers.length === 1 ? '' : 's'}`}>
        {run ? <><span className="dot" /> <span className="mono">{fmtElapsed(t.elapsedSeconds)}</span> <span className="small muted">{run.projectCode}</span></> : <><Pause size={14} strokeWidth={2} aria-hidden /> Timers ({t.timers.length})</>}
      </button>
      {(open || t.idle) && (
        <div className="menupop timerspop" role="menu">
          {t.idle && run && (
            <div className="stack idlebox">
              <p><strong>You were away for about {t.idle.minutes} min.</strong></p>
              <ul className="plain small muted"><li>{tr("Keep — count the idle time as work")}</li><li>{tr("Drop idle time — save up to the idle moment, then restart")}</li><li>{tr("Stop at idle — save up to the idle moment and stop")}</li></ul>
              <div className="row">
                <button className="button" onClick={t.dismissIdle}>{tr("Keep")}</button>
                <button className="button primary" onClick={async () => { const cur = run; await t.stop(cur.projectId, { endedAt: t.idle!.since }); await t.start({ projectId: cur.projectId, taskId: cur.taskId, notes: cur.notes, billable: cur.billable }) }}>Drop idle time</button>
                <button className="button" onClick={() => void t.stop(run.projectId, { endedAt: t.idle!.since })}>Stop at idle</button>
              </div>
            </div>
          )}
          {t.timers.map((tm) => (
            <div key={tm.projectId} className={`timerrow ${tm.running ? 'running' : ''}`}>
              <div className="stack" style={{ gap: 0, minWidth: 0 }}>
                <span><strong>{tm.projectCode}</strong> <span className="mono">{fmtElapsed(t.elapsedFor(tm))}</span>{tm.running && <span className="dot" style={{ marginLeft: '.35rem' }} />}</span>
                <span className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tm.taskKey ? `${tm.taskKey} ` : ''}{tm.notes ?? tm.taskTitle ?? tm.projectName}</span>
              </div>
              <span className="row" style={{ gap: '.25rem' }}>
                {tm.running
                  ? <button className="button sm" aria-label={tr("Pause")} data-tip={tr("Pause; time is kept")} onClick={() => void t.pause(tm.projectId)}><Pause size={14} aria-hidden /></button>
                  : <button className="button sm" aria-label={tr("Resume")} data-tip={tr("Resume (pauses the running timer)")} onClick={() => void t.start({ projectId: tm.projectId })}><Play size={14} aria-hidden /></button>}
                <button className="button sm primary" aria-label={tr("Stop and save")} data-tip={tr("Stop and save one time entry for the total")} onClick={() => void t.stop(tm.projectId)}><Square size={14} aria-hidden /></button>
              </span>
            </div>
          ))}
          <Link to="/time" className="small" onClick={() => setOpen(false)} data-tip={tr("Open the Time page: edit timers, notes, tasks")}>Manage on the Time page</Link>
          {t.error && <p className="bad small">{t.error}</p>}
        </div>
      )}
    </div>
  )
}
